import { queryKeys } from "@healthos/client-data";
import type { api } from "@healthos/schemas";
import { DAY_ZERO_COACHMARKS, DAY_ZERO_STOPS } from "@healthos/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { client, jsonOrThrow } from "../api/healthos";

export const dayZeroQueryKey = ["day-zero", "state"] as const;

/**
 * The Day Zero tour cursor. Read as a query rather than off the attention SSE
 * stream: advancing a stop is a client write, and the stream has no way to
 * push the next stop back in response to it.
 */
export function useDayZeroTour(enabled = true) {
  const queryClient = useQueryClient();

  const { data, isError } = useQuery({
    queryKey: dayZeroQueryKey,
    queryFn: async () =>
      jsonOrThrow<api.DayZeroStateResponse>(
        await client().api["day-zero"].state.$get(),
        "GET /api/day-zero/state",
      ),
    enabled,
    staleTime: 30_000,
  });

  const advance = useMutation({
    mutationFn: async (key: string) =>
      jsonOrThrow<api.DismissExplainerResponse>(
        await client().api.explainers.dismissals[":key"].$put({ param: { key } }),
        "PUT /api/explainers/dismissals/:key",
      ),
    // Move the cursor before the round trip. Taking a stop navigates
    // immediately, so without this the next screen reads a cursor still
    // pointing at the stop just taken and flashes a toast telling the member
    // to go where they already are.
    onMutate: (key: string) => {
      const at = DAY_ZERO_STOPS.findIndex((stop) => stop.key === key);
      if (at < 0) return;
      queryClient.setQueryData<api.DayZeroStateResponse>(dayZeroQueryKey, {
        started: true,
        stop: DAY_ZERO_STOPS[at + 1] ?? null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dayZeroQueryKey });
      // The Today card is projected from ambient, so it has to move with the
      // cursor or the member lands back on Today looking at the stop they
      // just finished.
      void queryClient.invalidateQueries({ queryKey: queryKeys.ambient });
    },
  });

  const skip = useMutation({
    mutationFn: async () =>
      jsonOrThrow<api.DayZeroSkipResponse>(
        await client().api["day-zero"].skip.$post(),
        "POST /api/day-zero/skip",
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dayZeroQueryKey });
      void queryClient.invalidateQueries({ queryKey: queryKeys.explainerDismissals });
    },
  });

  const stop = data?.stop ?? null;
  const started = data?.started ?? false;
  const loaded = data !== undefined;
  const advanceStop = (key: string) => advance.mutateAsync(key);

  return {
    loaded,
    started,
    stop,
    // Callers that hold something back until the run is over need to know the
    // difference between "not answered yet" and "will never answer": without
    // it, one failed read holds that thing back for the whole session.
    failed: isError,
    advance: advanceStop,
    skip: () => skip.mutateAsync(),
  };
}

// The coach marks anchor to the sidebar rail, which is hidden below this width,
// so they never run on a narrow screen. Anything waiting on them has to treat
// narrow as "nothing left to wait for", or it waits forever.
const COACHMARK_RAIL_BREAKPOINT = 768;

function useWideEnoughForCoachmarks(): boolean {
  const [wide, setWide] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= COACHMARK_RAIL_BREAKPOINT,
  );
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= COACHMARK_RAIL_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return wide;
}

/**
 * Whether any part of the first run is still to come: a stop on the cursor, or
 * a coach mark not yet seen. The stops are only half of it, so callers that
 * checked the cursor alone let the brief-ready toast and the mobile-app modal
 * arrive on top of the coach marks, which is the run competing with itself.
 *
 * Loading counts as active, so nothing can slip out in the gap before the
 * answer arrives, and a member who has long finished sees neither query pend.
 */
export function useDayZeroRunActive(): boolean {
  const { loaded, stop, failed } = useDayZeroTour();
  const wideEnough = useWideEnoughForCoachmarks();
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.explainerDismissals,
    queryFn: async () =>
      jsonOrThrow<api.ListExplainerDismissalsResponse>(
        await client().api.explainers.dismissals.$get(),
        "GET /api/explainers/dismissals",
      ),
    staleTime: 60_000,
  });

  if (failed) return false;
  if (!loaded || stop !== null) return true;
  if (!wideEnough) return false;
  // Fail open. This hook only decides whether something waits its turn, so a
  // failed or empty read must never be the reason a member stops seeing their
  // toasts and prompts entirely; holding them forever is far worse than
  // showing one a moment early.
  if (isError || !data) return false;
  if (isPending) return true;
  const seen = new Set(data.dismissedKeys);
  return DAY_ZERO_COACHMARKS.some((mark) => !seen.has(mark.key));
}
